import {useEffect, useRef, useState} from "react"

// ---- Shared types -----------------------------------------------------------

export type AttemptMethodStatsCycle = {
    method: string
    cycle: number
    sent: number
    pendingCallback: number
    failed: number
    skipped: number
}

export type DeliveryAttemptsSnapshot = {
    attempts: AttemptMethodStatsCycle[]
}

export type ResponsesCycleStats = {
    cycle: number
    responded: number
    responseValues: Record<string, number>
}

export type ResponsesSnapshot = {
    total: number
    cycleTotals: number[]  // per-cycle send counts, index 0 = cycle 1
    cycles: ResponsesCycleStats[]
}

export type BreakdownCategoryStats = {
    category: string
    cycle: number
    sent: number
    pendingCallback: number
    failed: number
    skipped: number
}

export type BreakdownSnapshot = {
    categories: BreakdownCategoryStats[]
}

export type AlertState = {
    status: string
    currentCycle: number
    startedAt: string | null
    completedAt: string | null
    cancelledAt: string | null
    expiredAt: string | null
    nextCycleAt: string | null
    broadcastConfig: {
        broadcastDuration: number
        cycleCount: number
        cycleInterval: number
        deliveryMethodInterval: number
    } | null
    alertConfig: {deliveryMethods: string[]} | null
}

// ---- WS URL helper ----------------------------------------------------------

function wsURL(alertID: string, path: string): string {
    const base = process.env.REACT_APP_PLATFORM_API ?? ""
    const wsBase = base.replace(/^https/, "wss").replace(/^http/, "ws")
    return `${wsBase}/api/siren/alerts/${alertID}/${path}`
}

// ---- Generic reconnecting WS hook ------------------------------------------

function useAlertWS<T>(alertID: string | undefined, path: string, msgType: string): {data: T | null; connected: boolean} {
    const [data, setData] = useState<T | null>(null)
    const [connected, setConnected] = useState(false)
    const wsRef = useRef<WebSocket | null>(null)

    useEffect(() => {
        if (!alertID) return

        let destroyed = false
        let retryTimeout: ReturnType<typeof setTimeout> | null = null
        const RETRY_DELAY_MS = 3000

        function connect() {
            if (destroyed) return
            const socket = new WebSocket(wsURL(alertID!, path))
            wsRef.current = socket

            socket.onopen = () => { if (!destroyed) setConnected(true) }

            socket.onclose = () => {
                if (destroyed) return
                setConnected(false)
                retryTimeout = setTimeout(connect, RETRY_DELAY_MS)
            }

            socket.onerror = () => { if (!destroyed) setConnected(false) }

            socket.onmessage = (e) => {
                let msg: {type: string; data: T}
                try { msg = JSON.parse(e.data) } catch { return }
                if (msg.type === msgType) {
                    setData(msg.data)
                }
            }
        }

        connect()

        return () => {
            destroyed = true
            if (retryTimeout) clearTimeout(retryTimeout)
            wsRef.current?.close()
            wsRef.current = null
        }
    }, [alertID, path, msgType])

    return {data, connected}
}

// ---- Status WS hook (existing endpoint, kept for AlertInfoCard) -------------

type SirenAlert = {
    id: string
    name: string
    content: string
    smsContent: string
    useSeparateSMSContent: boolean
    smsSenderID?: string
    status: string
    sendAt: string
    createdBy?: string
    templateID?: string
    templateName?: string
    responseOptions: string[]
    deliveryMethods: string[]
    broadcastConfig: {
        cycleCount: number
        cycleInterval: number
        cycleIntervalDisplayFormat: string
        deliveryMethodInterval: number
        deliveryMethodIntervalDisplayFormat: string
        broadcastDuration: number
        broadcastDurationDisplayFormat: string
    } | null
    useDefaultBroadcastConfig: boolean
    useDefaultDeliveryMethods: boolean
}

export type StatusSnapshot = {
    type: "snapshot"
    alert: SirenAlert
    state: AlertState | null
}

type StateChangedEvent = {type: "state_changed"; data: {status: string; currentCycle: number; completedAt: string | null; cancelledAt: string | null; expiredAt: string | null}}
type CycleWaitingEvent = {type: "cycle_waiting"; data: {completedCycle: number; nextCycle: number; nextCycleAt: string}}
type StatusWsMsg = StatusSnapshot | StateChangedEvent | CycleWaitingEvent

export function useSirenAlertStatus(alertID: string | undefined): {
    snapshot: StatusSnapshot | null
    state: AlertState | null
    connected: boolean
    nextCycleAt: string | null
} {
    const [snapshot, setSnapshot] = useState<StatusSnapshot | null>(null)
    const [state, setState] = useState<AlertState | null>(null)
    const [connected, setConnected] = useState(false)
    const [nextCycleAt, setNextCycleAt] = useState<string | null>(null)
    const wsRef = useRef<WebSocket | null>(null)

    useEffect(() => {
        if (!alertID) return

        let destroyed = false
        let retryTimeout: ReturnType<typeof setTimeout> | null = null
        const RETRY_DELAY_MS = 3000

        function connect() {
            if (destroyed) return
            const socket = new WebSocket(wsURL(alertID!, "status/ws"))
            wsRef.current = socket

            socket.onopen = () => { if (!destroyed) setConnected(true) }
            socket.onclose = () => {
                if (destroyed) return
                setConnected(false)
                retryTimeout = setTimeout(connect, RETRY_DELAY_MS)
            }
            socket.onerror = () => { if (!destroyed) setConnected(false) }

            socket.onmessage = (e) => {
                let msg: StatusWsMsg
                try { msg = JSON.parse(e.data) } catch { return }

                switch (msg.type) {
                    case "snapshot": {
                        const snap = msg as StatusSnapshot
                        setSnapshot(snap)
                        setState(prev => {
                            const next = snap.state
                            if (!next) return prev
                            // Preserve terminal timestamps already set by state_changed — snapshots can be stale.
                            if (prev) {
                                return {
                                    ...next,
                                    completedAt: next.completedAt ?? prev.completedAt,
                                    cancelledAt: next.cancelledAt ?? prev.cancelledAt,
                                    expiredAt:   next.expiredAt   ?? prev.expiredAt,
                                    // Don't downgrade a terminal status with a stale in_progress snapshot.
                                    status: (["completed", "cancelled", "expired"].includes(prev.status) &&
                                             !["completed", "cancelled", "expired"].includes(next.status))
                                        ? prev.status : next.status,
                                }
                            }
                            return next
                        })
                        setNextCycleAt(snap.state?.nextCycleAt ?? null)
                        break
                    }
                    case "state_changed": {
                        const {status, currentCycle, completedAt, cancelledAt, expiredAt} = msg.data
                        setState(prev => prev
                            ? {...prev, status, currentCycle, completedAt: completedAt ?? prev.completedAt, cancelledAt: cancelledAt ?? prev.cancelledAt, expiredAt: expiredAt ?? prev.expiredAt}
                            : {status, currentCycle, startedAt: null, completedAt: completedAt ?? null, cancelledAt: cancelledAt ?? null, expiredAt: expiredAt ?? null, nextCycleAt: null, broadcastConfig: null, alertConfig: null}
                        )
                        break
                    }
                    case "cycle_waiting": {
                        const {nextCycleAt: at} = msg.data
                        setNextCycleAt(at)
                        setState(prev => prev ? {...prev, nextCycleAt: at} : prev)
                        break
                    }
                }
            }
        }

        connect()

        return () => {
            destroyed = true
            if (retryTimeout) clearTimeout(retryTimeout)
            wsRef.current?.close()
            wsRef.current = null
        }
    }, [alertID])

    return {snapshot, state, connected, nextCycleAt}
}

// ---- Stats hooks ------------------------------------------------------------

export function useSirenDeliveryAttempts(alertID: string | undefined): {data: DeliveryAttemptsSnapshot | null} {
    const {data} = useAlertWS<DeliveryAttemptsSnapshot>(alertID, "delivery-attempts/ws", "delivery_attempts_update")
    return {data}
}

export function useSirenResponses(alertID: string | undefined): {data: ResponsesSnapshot | null} {
    const {data} = useAlertWS<ResponsesSnapshot>(alertID, "responses/ws", "responses_update")
    return {data}
}

export function useSirenBreakdown(alertID: string | undefined): {data: BreakdownSnapshot | null} {
    const {data} = useAlertWS<BreakdownSnapshot>(alertID, "delivery-breakdown/ws", "breakdown_update")
    return {data}
}
